/**
 * DuplicateAlert - Shows duplicate CV notifications with merge/dismiss actions.
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Merge, X, Loader2, Search } from 'lucide-react';
import {
  Button,
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
} from '@aaas/shared';
import {
  useCVDuplicatesQuery,
  useCheckDuplicatesMutation,
  useMergeDuplicateMutation,
  useDismissDuplicateMutation,
} from '../hooks/useCVDuplicateQueries';
import type { CVDuplicate } from '../types';

interface DuplicateAlertProps {
  cvId: string;
  workspaceId: string | undefined;
  idToken: string | undefined;
  onMergingChange?: (isMerging: boolean) => void;
}

function DuplicateItem({
  duplicate,
  currentCvId,
  onMerge,
  onDismiss,
  isMerging,
  isDismissing,
  t,
}: {
  duplicate: CVDuplicate;
  currentCvId: string;
  onMerge: (duplicateId: string) => void;
  onDismiss: (duplicateId: string) => void;
  isMerging: boolean;
  isDismissing: boolean;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  const otherCv =
    duplicate.cv_a.id === currentCvId ? duplicate.cv_b : duplicate.cv_a;

  const matchTypeLabel = t(`duplicate.matchType.${duplicate.match_type}`) || duplicate.match_type;

  return (
    <div className="flex flex-col gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
      <div className="flex items-start gap-2">
        <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0 text-sm">
          <p className="text-amber-800">
            {t('duplicate.duplicateMatch', { type: matchTypeLabel, value: duplicate.match_value })}
          </p>
          <p className="text-amber-700 mt-1">
            {t('duplicate.duplicateCv')}{' '}
            <Link
              to={`/ats/cvs/${otherCv.id}`}
              className="font-medium text-blue-600 hover:text-blue-800 underline"
            >
              {otherCv.name}
            </Link>
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 ml-6">
        <Button
          size="sm"
          variant="outline"
          onClick={() => onMerge(duplicate.id)}
          disabled={isMerging || isDismissing}
          className="h-7 px-2 text-xs border-blue-300 text-blue-700 hover:bg-blue-50"
        >
          {isMerging ? (
            <Loader2 className="h-3 w-3 animate-spin mr-1" />
          ) : (
            <Merge className="h-3 w-3 mr-1" />
          )}
          {t('duplicate.mergeButton')}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onDismiss(duplicate.id)}
          disabled={isMerging || isDismissing}
          className="h-7 px-2 text-xs text-gray-500 hover:text-gray-700"
        >
          {isDismissing ? (
            <Loader2 className="h-3 w-3 animate-spin mr-1" />
          ) : (
            <X className="h-3 w-3 mr-1" />
          )}
          {t('duplicate.dismissButton')}
        </Button>
      </div>
    </div>
  );
}

export function DuplicateAlert({ cvId, workspaceId, idToken, onMergingChange }: DuplicateAlertProps) {
  const navigate = useNavigate();
  const { t } = useTranslation('ats');
  const [confirmMergeId, setConfirmMergeId] = useState<string | null>(null);

  const { data: duplicates } = useCVDuplicatesQuery({
    cvId,
    workspaceId,
    idToken,
    enabled: !!cvId && !!workspaceId && !!idToken,
  });

  const checkMutation = useCheckDuplicatesMutation({
    cvId,
    workspaceId,
    idToken,
  });

  const mergeMutation = useMergeDuplicateMutation({
    cvId,
    workspaceId,
    idToken,
  });

  const dismissMutation = useDismissDuplicateMutation({
    cvId,
    workspaceId,
    idToken,
  });

  const handleMerge = (duplicateId: string) => {
    setConfirmMergeId(duplicateId);
  };

  const handleConfirmMerge = () => {
    if (!confirmMergeId) return;
    onMergingChange?.(true);
    mergeMutation.mutate(confirmMergeId, {
      onSuccess: (result) => {
        setConfirmMergeId(null);
        if (result.deleted_cv_id === cvId) {
          navigate(`/ats/cvs/${result.surviving_cv_id}`);
        } else {
          onMergingChange?.(false);
        }
      },
      onError: (error) => {
        setConfirmMergeId(null);
        onMergingChange?.(false);
        alert(error instanceof Error ? error.message : t('duplicate.mergeFailed'));
      },
    });
  };

  const handleDismiss = (duplicateId: string) => {
    dismissMutation.mutate(duplicateId);
  };

  const handleCheckDuplicates = () => {
    checkMutation.mutate();
  };

  const hasDuplicates = duplicates && duplicates.length > 0;

  return (
    <>
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className={`text-sm font-semibold flex items-center gap-2 ${hasDuplicates ? 'text-amber-800' : 'text-gray-700'}`}>
            {hasDuplicates ? (
              <>
                <AlertTriangle className="h-4 w-4" />
                {t('duplicate.detected', { count: duplicates.length })}
              </>
            ) : (
              t('duplicate.title')
            )}
          </h3>
          <Button
            size="sm"
            variant="outline"
            onClick={handleCheckDuplicates}
            disabled={checkMutation.isPending}
            className="h-7 px-2 text-xs"
          >
            {checkMutation.isPending ? (
              <Loader2 className="h-3 w-3 animate-spin mr-1" />
            ) : (
              <Search className="h-3 w-3 mr-1" />
            )}
            {t('duplicate.checkButton')}
          </Button>
        </div>

        {checkMutation.isSuccess && !hasDuplicates && (
          <p className="text-sm text-green-600">{t('duplicate.noDuplicates')}</p>
        )}

        {checkMutation.isError && (
          <p className="text-sm text-red-600">
            {checkMutation.error instanceof Error
              ? checkMutation.error.message
              : t('duplicate.checkError')}
          </p>
        )}

        {hasDuplicates && (
          <div className="space-y-2">
            {duplicates.map((dup) => (
              <DuplicateItem
                key={dup.id}
                duplicate={dup}
                currentCvId={cvId}
                onMerge={handleMerge}
                onDismiss={handleDismiss}
                isMerging={mergeMutation.isPending && mergeMutation.variables === dup.id}
                isDismissing={dismissMutation.isPending && dismissMutation.variables === dup.id}
                t={t}
              />
            ))}
          </div>
        )}
      </div>

      {/* Merge Confirmation Dialog */}
      <AlertDialog open={!!confirmMergeId} onOpenChange={(open) => {
        if (!open && !mergeMutation.isPending) setConfirmMergeId(null);
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('duplicate.confirmMergeTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('duplicate.confirmMergeDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={mergeMutation.isPending}>{t('duplicate.confirmMergeCancel')}</AlertDialogCancel>
            <Button
              onClick={handleConfirmMerge}
              disabled={mergeMutation.isPending}
            >
              {mergeMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Merge className="h-4 w-4 mr-2" />
              )}
              {t('duplicate.mergeButton')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
